/**
 * La ficha de un servicio reservable.
 *
 * La misma planta que la ficha de una oferta, porque es la misma clase de cosa:
 * barra de sección, pestañas —Citas, Página y Ajustes— y debajo dos columnas.
 * A la izquierda la pestaña abierta; a la derecha, fija y a la vista desde las
 * tres, la tarjeta del servicio: portada, nombre, estado, descripción, cuánto
 * dura, para cuánta gente, cuánto cuesta y los mandos del enlace.
 *
 * ── Se lee aquí, se cambia en el asistente ───────────────────────────────────
 * Aquí hubo campos: el nombre en línea y tres sueltos —tema de la página,
 * correo y teléfono— con su propia barra de «Guardar». Se fueron al asistente,
 * que ya preguntaba todo lo demás, y con ellos el motivo de tener un formulario
 * dentro de una ficha. Cada renglón de «Ajustes» abre el asistente por su paso.
 *
 * Lo que sigue siendo inmediato son las acciones de un clic —la portada,
 * publicar y borrar—: lo que se pulsa es lo que pasa, y meterlas en un borrador
 * obligaría a guardar para ver el efecto de algo ya decidido al pulsarlo.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { offerShortDescription } from '@webbuilder/schemas'
import { useTabThumb } from '../../../hooks/useTabThumb'
import {
  IconAlertCircle,
  IconAlignLeft,
  IconArrowBackUp,
  IconArrowLeft,
  IconBell,
  IconCalendar,
  IconCalendarCheck,
  IconCalendarEvent,
  IconCalendarPlus,
  IconCalendarX,
  IconChartBar,
  IconClock,
  IconCopy,
  IconCurrencyDollar,
  IconExternalLink,
  IconEye,
  IconFingerprint,
  IconForms,
  IconHistory,
  IconHourglass,
  IconListDetails,
  IconLoader2,
  IconMail,
  IconPalette,
  IconPencil,
  IconPhone,
  IconPhotoPlus,
  IconSettings,
  IconTrash,
  IconUserCheck,
  IconUsers,
  IconWorld,
} from '@tabler/icons-react'
import { OfferCard } from './OfferCard'
import { OfferSalesNotice } from './OfferSalesNotice'
import { OfferPageSettings } from './OfferPageSettings'
import { OfferAppearanceSettings } from './OfferAppearanceSettings'
import type { ProjectTheme } from '../../../lib/preview/types'
import { getPolicyHistory, type PolicyVersion } from '../../../lib/api'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { useUnsavedGuard } from '../../../lib/unsavedChanges'
import { ImageLibraryPickerDialog } from '../../ImageLibraryPickerDialog'
import { useToast } from '../../Toast'
import { BookingsTable } from './BookingsTable'
import { SplitDonut, StatCard } from './analyticsShared'
import { NewBookingServiceDialog } from './NewBookingServiceDialog'
import { NewBookingDialog } from './NewBookingDialog'
import { ConfirmDialog } from '../ConfirmDialog'
import { bookingError, minutesLabel } from './bookingShared'
import { activeLanguage, useI18n } from '../../../lib/i18n'
import { longDate } from '../../../lib/dates'
import { Notice } from '../Notice'
import { OfferStatusSelect, PublishBlockDialog } from './OfferStatusSelect'
import { PlanLimitModal } from '../PlanLimitModal'
import type { PublishBlock } from './offerShared'
import type { BillingPlan } from '../../../lib/api'
import {
  deleteOffer,
  updateBooking,
  updateOffer,
  type Booking,
  type BookingResource,
  type Offer,
} from '../../../lib/api'
import { useOfferFields } from './OfferCustomFields'
import { RecordPhotoStrip } from './RecordPhotoStrip'
import { BookingServiceTeam } from './BookingServiceTeam'

/** Escondida mientras se prueba la plantilla de reservas del editor. Ver `OfferDetail`. */
const HIDE_LOOK_TAB = true

/** Un campo numérico de la agenda: etiqueta, ayuda y anotado en el borrador al salir. */

export function BookingServiceDetail({
  projectId, token, service, bookings, resources, timezone, siteOrigin, scheduleLoading: _scheduleLoading,
  publishBlock, canConnectStripe, plan, publishedOffers, connectingStripe, onConnectStripe, onOpenPlans, onOpenPublish,
  onBack, onChange, onDeleted, onResourcesChange, onOpenBooking, onBookingsChange, theme,
  backLabel,
}: {
  /** El tema del sitio: los tokens que ofrece la pestaña «Apariencia». */
  theme?: ProjectTheme | undefined
  projectId: string
  token: string
  service: Offer
  /** Las citas de ESTE servicio, ya filtradas por quien llama. */
  bookings: Booking[]
  resources: BookingResource[]
  timezone: string
  siteOrigin: string
  /** Ya no se usa: el horario se edita en el asistente. La acepta para no
   *  obligar a `BookingsSection` a saber qué pestañas tiene esta ficha. */
  scheduleLoading: boolean
  /** Qué impide publicarlo, si es que algo lo impide. Lo decide `BookingsSection`. */
  publishBlock: PublishBlock
  canConnectStripe?: boolean
  /** De paso al asistente, que decide si «publicado» cabe en el plan. */
  plan: BillingPlan
  publishedOffers: number
  connectingStripe?: boolean | undefined
  onConnectStripe: () => void
  onOpenPlans: () => void
  /** Al editor, a publicar: es de donde nace la dirección del sitio. */
  onOpenPublish?: (() => void) | undefined
  onBack: () => void
  onChange: (offer: Offer) => void
  /** Ya no existe: quien tiene la lista debe quitarlo y volver a ella. */
  onDeleted: (offerId: string) => void
  onResourcesChange: (resources: BookingResource[]) => void
  onOpenBooking: (bookingId: string) => void
  onBookingsChange: () => void
  /**
   * A qué lista vuelve el botón de atrás.
   *
   * Esta misma ficha se monta también dentro del catálogo de ofertas, y es lo
   * único que cambia de verdad entre los dos sitios: el título nombra al
   * servicio en los dos, porque nombra lo que tienes delante y eso no depende
   * de por dónde entraste. Sin esto, la lista de Reservas.
   */
  backLabel?: string | undefined
}) {
  const { showToast } = useToast()
  const { t } = useI18n()
  const tabsRef = useRef<HTMLDivElement | null>(null)
  const [saving, setSaving] = useState('')
  /* El motivo entero y no su nombre: el del cupo lleva el plan encima, que es
     lo que su ventana necesita para ofrecer el siguiente. */
  const [explainingPublish, setExplainingPublish] = useState<PublishBlock>(null)
  /* Abre en Citas.
     El calendario de este servicio ya no está aquí: vive en Reservas →
     Calendario, con las de todos los demás al lado. Dentro de una ficha
     enseñaba un mes entero para pintar las citas de UN servicio, que es
     justamente la vista que menos falta hace teniendo la lista debajo — y
     obligaba a mirar dos calendarios distintos para saber qué hay el martes. */
  const [tab, setTab] = useState<'clientes' | 'analiticas' | 'avisos' | 'pagina' | 'apariencia' | 'ajustes'>('clientes')

  /**
   * Las cifras de ESTE servicio, calculadas de sus propias citas.
   *
   * Sin pedir nada: la lista ya está aquí, filtrada por quien llama. La sección
   * de Analíticas de reservas mide el negocio entero y con tramos de fechas;
   * aquí la pregunta es otra —«¿cómo va esto?»— y se responde con lo que ya se
   * está enseñando en la pestaña de al lado, así que las dos no pueden
   * contradecirse.
   *
   * Los apartados sin confirmar (`held`) no cuentan en ninguna cifra, igual que
   * en Analíticas: son huecos que caducaron, no citas.
   */
  const stats = useMemo(() => {
    const real = bookings.filter((row) => row.status !== 'held')
    const completed = real.filter((row) => row.status === 'completed').length
    const noShow = real.filter((row) => row.status === 'no_show').length
    const cancelled = real.filter((row) => row.status === 'cancelled').length
    const now = Date.now()
    const upcoming = real.filter(
      (row) => row.status === 'confirmed' && new Date(row.startsAt).getTime() >= now,
    ).length
    /* Sólo las que ya pasaron cuentan para la asistencia: una cita de la semana
       que viene no es un fallo, todavía no ha tenido ocasión de serlo. */
    const resolved = completed + noShow
    return {
      total: real.length,
      completed,
      noShow,
      cancelled,
      upcoming,
      resolved,
      attendanceRate: resolved > 0 ? completed / resolved : 0,
      cancellationRate: real.length > 0 ? cancelled / real.length : 0,
    }
  }, [bookings])

  /*
   * Cuánto cuesta y cómo se cobra son DOS datos, no uno.
   *
   * Se enseñaba sólo «Sin cobro en línea» en cuanto el servicio se pagaba en el
   * lugar, y eso contesta CÓMO llevándose por delante el CUÁNTO: un servicio
   * que se cobra en persona sigue teniendo precio, y es el dato que se viene a
   * mirar de pasada. Ahora se dicen los dos cuando los hay.
   *
   * Sin precio guardado y con cobro en el lugar sólo queda la nota, que es lo
   * correcto: ahí no hay cifra que esconder.
   */
  const activePrice = service.prices.find((row) => row.active) ?? null
  const onsite = service.booking.paymentMode === 'onsite'
  /* Quiénes lo dan: su lista (sin los inactivos), o su recurso único, o el primero. */
  const activeTeam = resources.filter((row) => row.active)
  const serviceTeam = (() => {
    const active = activeTeam
    const listed = active.filter((row) => service.booking.resourceIds?.includes(row.id))
    if (listed.length) return listed
    const single = resources.find((row) => row.id === service.booking.resourceId) ?? active[0]
    return single ? [single] : []
  })()
  const priceLabel = activePrice?.formatted ?? (onsite ? null : t('bookings.svc.detail.priceNone'))
  const onsiteNote = onsite ? t('bookings.svc.detail.priceOnsite') : null
  /* El historial se pide al abrir Detalles, no al abrir la ficha: casi siempre se
     viene a otra cosa, y es una consulta que nadie ha mirado nunca. */
  const [history, setHistory] = useState<PolicyVersion[] | null>(null)
  useEffect(() => {
    if (tab !== 'ajustes' || history) return
    let active = true
    getPolicyHistory(projectId, service.id, token)
      .then((rows) => { if (active) setHistory(rows) })
      /* Un historial que no carga no puede romper la pantalla: se queda vacío. */
      .catch(() => { if (active) setHistory([]) })
    return () => { active = false }
  }, [tab, history, projectId, service.id, token])
  const [addOpen, setAddOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  /* «Por completar» abre directo su asistente (ver `OfferDetail`). */
  const [editOpen, setEditOpen] = useState(Boolean(service.setupPending))
  /** Por qué paso abre el asistente. Lo fija el atajo que se pulsó. */
  const [editStep, setEditStep] = useState(0)

  /**
   * Cuántos días de la semana tienen alguna franja abierta.
   *
   * Se cuentan los días DISTINTOS y no las franjas: un martes partido en mañana
   * y tarde son dos reglas y un solo día abierto, y decir «8 días» en una semana
   * de siete es lo que hace que nadie vuelva a fiarse del resumen.
   */
  const openDaysLabel = (() => {
    const own = resources.find((row) => row.id === service.booking.resourceId) ?? resources[0]
    const days = new Set((own?.rules ?? []).map((rule) => rule.weekday))
    if (days.size === 0) return t('bookings.svc.detail.daysNone')
    return days.size === 1
      ? t('bookings.svc.detail.daysOne')
      : t('bookings.svc.detail.daysMany', { count: days.size })
  })()
  const [imagePickerOpen, setImagePickerOpen] = useState(false)

  /*
   * Aquí vivía un borrador con su barra de «Guardar».
   *
   * Hacía falta porque la ficha tenía campos: el nombre en línea y tres sueltos
   * —tema de la página, correo y teléfono— que no preguntaba nadie más. Esos
   * tres se fueron al asistente, que es donde se contesta todo lo demás del
   * servicio, y con ellos se fue el motivo de tener aquí un formulario.
   *
   * Lo que queda son acciones de un clic —la portada y publicar—, que no
   * necesitan borrador: lo que se pulsa es lo que pasa. Es la misma planta que
   * la ficha de una oferta, que hizo este mismo camino antes.
   */
  /* Salir sigue preguntando por si otra pantalla del panel tiene algo a medias
     —el registro es común— y lo usa también el cambio de pestaña, porque la de
     «Página» sí escribe. */
  const blockUnsaved = useUnsavedGuard()
  /* El horario es la otra mitad de lo que se edita aquí, y se guarda por su
     cuenta contra otro sitio del servidor. Se maneja desde la misma barra: dos
     barras iguales en la misma pestaña —una del horario y otra de los márgenes
     de la cita— no se distinguen entre sí. */
  /*
   * Dónde va la pastilla: se mide, no se calcula.
   *
   * `useLayoutEffect` y no `useEffect` porque escribe posición: con el segundo
   * se pinta un fotograma con la pastilla donde estaba y se ve el salto.
   *
   * Se remide al cambiar de pestaña y al cambiar de tamaño la ventana, y también
   * cuando cambia el número de citas: ese contador ensancha «Citas» y sin esto
   * la pastilla se quedaba corta al confirmar la primera reserva.
   */
  useTabThumb(tabsRef, `${tab}:${bookings.length}`)

  /** Volver a la lista, salvo que haya cambios sin guardar: entonces se avisa. */
  const guardedBack = () => {
    if (blockUnsaved()) return
    onBack()
  }

  /**
   * Abre el asistente por un paso.
   *
   * Cada fila del resumen abre por el suyo: quien pulsa «Precio» quiere el paso
   * del precio, no el principio de un formulario de siete. Y pasa por el mismo
   * guardián que salir, porque los ajustes de página se escriben en la pestaña
   * de al lado y el asistente carga lo GUARDADO.
   */
  /* Los campos personalizados de las reservas, para el resumen. */
  const { fields: offerFields } = useOfferFields(projectId, token)
  const openWizard = (step: number) => {
    if (blockUnsaved()) return
    setEditStep(step)
    setEditOpen(true)
  }

  const publicUrl = siteOrigin ? `${siteOrigin}/book/${service.slug}` : ''
  /* Las que quedarían sin servicio si se borra. El servidor las vuelve a contar
     al borrar —esto es sólo para avisar antes— porque entre que se abre la
     pantalla y se confirma pueden entrar más. */
  const upcomingCount = bookings.filter(
    (row) => (row.status === 'held' || row.status === 'confirmed') && Date.parse(row.startsAt) >= Date.now(),
  ).length
  const live = service.status === 'active'
  // La oferta puede apuntar a un recurso con zona propia. Es la misma
  // resolución que hace el servidor al calcular disponibilidad.
  const bookingTimezone = (
    service.booking.resourceId
      ? resources.find((row) => row.id === service.booking.resourceId)?.timezone
      : resources[0]?.timezone
  ) ?? timezone

  const patch = async (
    body: Parameters<typeof updateOffer>[2],
    field: string,
  ) => {
    setSaving(field)
    try {
      onChange(await updateOffer(projectId, service.id, body, token))
    } catch (error) {
      showToast(bookingError(error), 'error')
    } finally {
      setSaving('')
    }
  }


  const copyLink = async () => {
    if (!publicUrl) return
    try {
      await navigator.clipboard.writeText(publicUrl)
      showToast(t('common.linkCopied'))
    } catch {
      showToast(t('common.copyFailed'), 'error')
    }
  }

  return (
    <div className="dash-projects-view dash-pages-view dash-booking-detail-view">
      {/* La misma barra que la ficha de una oferta: de dónde vienes arriba y qué
          clase de cosa estás mirando debajo. El nombre del servicio no va aquí
          —está en la cabecera de abajo, con su portada— porque decirlo dos veces
          en dos tamaños distintos no lo dice mejor. */}
      <DashboardPageHeader
        /* El nombre del servicio, no el de la sección. Decía «Bookings» sobre
           «Scheduled services»: eso nombra la lista de la que vienes, no la
           cosa que tienes delante, así que con tres fichas abiertas seguidas la
           barra era idéntica en las tres. De dónde vienes lo sigue diciendo el
           botón de volver, que es donde se pulsa para ir. */
        title={service.title || t('common.untitled')}
        subtitle={t('offers.kind.booking')}
        actions={(
          <button className="dash-secondary-btn dash-detail-back" type="button" onClick={guardedBack}>
            <IconArrowLeft size={14} /> {backLabel ?? t('bookings.svc.detail.back')}
          </button>
        )}
      />

      {/* Las pestañas van FUERA de las dos columnas: son de la ficha entera,
          no de la columna izquierda. Dentro de ella, la tarjeta de la derecha
          arrancaba a su misma altura y quedaba un renglón por encima del
          contenido; así las dos columnas empiezan donde acaban las pestañas. */}
      <div
        className="dash-booking-service-tabs dash-tabs-icon-collapse"
        ref={tabsRef}
        role="tablist"
        aria-label={t('bookings.svc.detail.tabsAria')}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
          const tabs = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'))
          const current = tabs.indexOf(document.activeElement as HTMLButtonElement)
          if (current < 0) return
          event.preventDefault()
          const direction = event.key === 'ArrowRight' ? 1 : -1
          const next = tabs[(current + direction + tabs.length) % tabs.length]
          next?.focus()
          next?.click()
        }}
      >
        {/*
          La pastilla que se desliza, medida y no calculada.

          Con pestañas de anchos distintos —«Citas» y «Contacto» no miden lo
          mismo— no vale repartir el ancho entre tres: quedaría descuadrada en
          todas menos una. Se mide la activa y se le pasan su sitio y su ancho
          al CSS.

          Va antes que los botones en el DOM y por debajo con z-index, para que
          el texto de la activa se lea encima de ella.
        */}
        <span className="dash-booking-service-tabs-thumb" aria-hidden="true" />
        {/* Las mismas tres que en una oferta y en el mismo orden: primero lo
            que ha vendido —aquí, quién ha reservado—, luego su página y al final
            lo que el servicio ES. Falta «Analíticas», que en una oferta sale de
            sus cobros; las de una agenda se miden con citas y todavía no existen. */}
        {([
          ['clientes', 'bookings.svc.detail.tab.bookings', IconCalendarEvent],
          /* Los números de ESTE servicio. En una oferta salen de sus cobros;
             los de una agenda se miden con citas, y por eso faltaba: no había
             de dónde sacarlos hasta que la ficha recibió las suyas. Va segunda,
             el mismo sitio que en la ficha de una oferta. */
          ['analiticas', 'offers.detail.tab.analytics', IconChartBar],
          ['avisos', 'bookings.svc.detail.tab.notices', IconBell],
          /* La PÁGINA del servicio: su dirección, cómo se titula en un buscador
             y qué se ve al pegar el enlace. La misma pestaña que la ficha de una
             oferta, y a propósito la misma: `/book/<slug>` es una página
             pública igual que la de comprar. */
          ['pagina', 'offers.detail.tab.page', IconWorld],
          /* Cómo se VE su página de reservar —colores, textos, la etiqueta y el
             botón—, con los tokens del sitio. La misma pestaña que en una oferta,
             sobre la página que le toca a un servicio con agenda. */
          /* ESCONDIDA a prueba (2026-10-05), como en `OfferDetail`: el aspecto
             de la página de reservar pasa al editor («Páginas → Reservas →
             Página de cada reserva»). Ver `HIDE_LOOK_TAB`. */
          ...(HIDE_LOOK_TAB ? [] : [['apariencia', 'offers.detail.tab.look', IconPalette] as const]),
          ['ajustes', 'offers.detail.tab.settings', IconSettings],
        ] as const).map(([value, labelKey, Icon]) => (
          <button
            key={value}
            id={`booking-service-tab-${value}`}
            type="button"
            role="tab"
            aria-label={t(labelKey)}
            aria-selected={tab === value}
            aria-controls={`booking-service-panel-${value}`}
            tabIndex={tab === value ? 0 : -1}
            className={tab === value ? 'is-active' : ''}
            /* Con algo sin guardar, la pestaña no se mueve: la de «Página» se
               desmonta al salir de ella y se llevaría lo escrito. Parpadea la
               barra de arriba, que es donde está el botón de guardar. */
            onClick={() => { if (!blockUnsaved()) setTab(value) }}
          >
            <Icon size={15} />
            {/* Sin la cuenta de citas al lado. Una pestaña dice a dónde se va,
                y el número no cambiaba esa decisión: la lista que hay detrás lo
                repite al pie —«1 cita»— con sitio para decirlo entero. */}
            <span>{t(labelKey)}</span>
          </button>
        ))}
      </div>

      {/* En «Apariencia» sin la tarjeta de la derecha, como en una oferta: ahí
          se mira una página entera y la vista previa necesita el ancho. */}
      <div className={`dash-booking-service-layout${tab === 'apariencia' ? ' is-single' : ''}`}>
      <main
        id={`booking-service-panel-${tab}`}
        className={`dash-booking-service-main${tab === 'ajustes' || tab === 'pagina' || tab === 'avisos' ? ' dash-commerce-details' : ''}`}
        role="tabpanel"
        aria-labelledby={`booking-service-tab-${tab}`}
      >
        {/* Sin título ni frase de presentación: lo dice la pastilla de arriba,
            y la lista de abajo se explica sola. */}
      {tab === 'avisos' ? (
        <OfferSalesNotice
          projectId={projectId}
          token={token}
          offer={service}
          onChange={onChange}
          bookable
          team={activeTeam.length > 1 ? serviceTeam : undefined}
          onTeamChange={onResourcesChange}
          allResources={resources}
        />
      ) : tab === 'pagina' ? (
        <OfferPageSettings
          projectId={projectId}
          token={token}
          offer={service}
          siteOrigin={siteOrigin}
          onChange={onChange}
        />
      ) : tab === 'apariencia' ? (
        <OfferAppearanceSettings
          projectId={projectId}
          token={token}
          offer={service}
          theme={theme}
          siteOrigin={siteOrigin}
          onChange={onChange}
        />
      ) : tab === 'analiticas' ? (
        /* Las mismas piezas que las demás pantallas de analíticas —las tarjetas
           de cifra y el donut—, para que quien ya sabe leer una sepa leer ésta.
           Sin selector de fechas: lo que hay son las citas de este servicio, y
           un tramo que recortara la lista de al lado haría que las dos pestañas
           contaran cosas distintas del mismo sitio. */
        <div className="dash-booking-service-analytics">
          <div
            className="dash-svc-analytics-cards dash-auto-grid"
          >
            <StatCard
              label={t('bookingsAn.bookings')}
              value={String(stats.total)}
              icon={IconCalendarEvent}
              hint={stats.upcoming > 0
                ? t('bookingsAn.bookings.upcoming', { count: String(stats.upcoming) })
                : stats.total === 0 ? t('bookingsAn.noBookings') : undefined}
            />
            <StatCard
              label={t('bookingsAn.attendance')}
              value={stats.resolved > 0 ? `${Math.round(stats.attendanceRate * 100)}%` : '—'}
              icon={IconUserCheck}
              hint={stats.resolved > 0
                ? t('bookingsAn.attendance.hint', { came: String(stats.completed), missed: String(stats.noShow) })
                : t('bookingsAn.attendance.none')}
            />
            <StatCard
              label={t('bookingsAn.cancellations')}
              /* Sin flecha de tendencia a propósito: un «subió» en verde sobre
                 las cancelaciones diría lo contrario de lo que pasó. */
              value={String(stats.cancelled)}
              icon={IconCalendarX}
              hint={stats.total > 0
                ? t('bookingsAn.cancellations.hint', { percent: `${Math.round(stats.cancellationRate * 100)}%` })
                : t('bookingsAn.noBookings')}
            />
          </div>

          <SplitDonut
            title={t('bookingsAn.outcome')}
            icon={IconCalendarCheck}
            unit={t('bookingsAn.unit.bookings')}
            slices={[
              { key: 'completed', label: t('bookingsAn.outcome.attended'), value: stats.completed, color: 'var(--dash-analytics-ink)' },
              { key: 'no_show', label: t('bookingsAn.outcome.noShow'), value: stats.noShow, color: 'var(--dash-analytics-ink-2)' },
              { key: 'cancelled', label: t('bookingsAn.outcome.cancelled'), value: stats.cancelled, color: 'var(--dash-analytics-ink-3)' },
            ]}
            empty={t('bookingsAn.outcome.empty')}
          />
        </div>
      ) : tab === 'clientes' ? (
        <>
          {service.status === 'draft' && bookings.length === 0 ? (
            <Notice
              tone="accent"
              title={t('bookings.svc.detail.draftNotice.title')}
              actions={
                <button
                  type="button"
                  className="dash-primary-btn"
                  disabled={saving === 'status'}
                  onClick={() => {
                    if (publishBlock !== null) {
                      setExplainingPublish(publishBlock)
                    } else {
                      void patch({ status: 'active' }, 'status')
                    }
                  }}
                >
                  {saving === 'status' ? <IconLoader2 size={15} className="dash-spin" /> : null}
                  {t('bookings.svc.detail.draftNotice.publish')}
                </button>
              }
            >
              <p>{t('bookings.svc.detail.draftNotice.body')}</p>
            </Notice>
          ) : null}

          <BookingsTable
            bookings={bookings}
            loading={false}
            failed={false}
            failureReason=""
            onReload={onBookingsChange}
            onOpen={onOpenBooking}
            onCreate={() => setAddOpen(true)}
            /* Todas estas citas son de este servicio: la columna repetiría su
               nombre en cada fila, y ese hueco es justo lo que le falta a la tabla
               para no convertirse en fichas dentro de esta columna. */
            hideService
            resources={resources}
            onStatusChange={async (booking, status) => {
              try {
                await updateBooking(projectId, booking.id, { status }, token)
                /* Se recargan desde quien las tiene: esta ficha recibe las citas ya
                   filtradas, así que no puede reemplazar una por su cuenta. */
                onBookingsChange()
              } catch (error) {
                showToast(bookingError(error), 'error')
              }
            }}
          />
        </>
      ) : (
        /*
          Ajustes: la misma lista con la que termina el asistente.

          Y a propósito la misma que en la ficha de una oferta —sus clases, sus
          renglones, su «Editar» a la derecha—: lo que se lee aquí es lo que allí
          se acaba de contestar, así que enseñarlo de otra forma obligaría a
          reconocer dos veces los mismos datos.

          Esto sustituye a la columna fija de la derecha y a las cuatro tarjetas
          de recuadros que había debajo. Los recuadros repetían en otro formato
          lo que la columna ya decía —la duración salía dos veces, y en unidades
          distintas—, y la columna gastaba trescientos píxeles en todas las
          pestañas. Cada dato sale una vez, en su fila, con su atajo al paso del
          asistente donde se cambia.
        */
        <div className="dash-ofd-settings">
          <OfferCard
            className="dash-ofd-primary-card"
            icon={<IconClock size={15} />}
            title={t('bookings.svc.detail.card.service')}
            action={(
              <button type="button" className="dash-secondary-btn dash-summary-action" onClick={() => openWizard(0)}>
                <IconSettings size={13} /> {t('offers.detail.editAll')}
              </button>
            )}
          >
            <div className="dash-svc-review">
              {([
                [t('offers.new.shortDescriptionLabel'), offerShortDescription(service) || t('offers.detail.noDescription'), 0, 'shortDescription', IconAlignLeft],
                [t('offers.new.longDescriptionLabel'), service.description.trim() || t('offers.detail.noDescription'), 0, 'description', IconAlignLeft],
                [t('bookings.svc.detail.fact.duration'), service.booking.durationMinutes ? minutesLabel(service.booking.durationMinutes) : t('common.none'), 1, 'duration', IconClock],
                /* Las palabras del asistente y no unas propias: decía «1 persona»
                   donde él dice «Individual», y quien venía de configurarlo tenía
                   que traducir su propia respuesta para reconocerla. */
                [t('bookings.svc.detail.fact.capacity'), service.booking.capacity > 1
                  ? t('svcw.page.spots', { count: service.booking.capacity })
                  : t('svcw.set.single'), 1, 'capacity', IconUsers],
                /* «Sin cobro en línea» y no un guion: que se pague en el lugar es
                   una decisión, no un dato que falte. */
                [t('bookings.svc.detail.fact.price'),
                  [priceLabel, onsiteNote].filter(Boolean).join(' · '), 2, 'price', IconCurrencyDollar],
                [t('bookings.svc.detail.fact.schedule'), openDaysLabel, 4, 'schedule', IconCalendar],
                [t('bookings.svc.detail.fact.buffers'), service.booking.bufferBeforeMinutes || service.booking.bufferAfterMinutes
                  ? `${service.booking.bufferBeforeMinutes}m / ${service.booking.bufferAfterMinutes}m`
                  : t('bookings.svc.detail.policyWindowNone'), 1, 'buffers', IconHourglass],
                [t('bookings.svc.detail.fact.notice'), service.booking.minNoticeMinutes
                  ? minutesLabel(service.booking.minNoticeMinutes)
                  : t('bookings.svc.detail.policyWindowNone'), 1, 'notice', IconBell],
                [t('bookings.svc.detail.fact.form'), (service.booking.formFields ?? []).length > 0
                  ? t('bookings.svc.detail.formCount', { count: (service.booking.formFields ?? []).length })
                  : t('bookings.svc.detail.formNone'), 3, 'form', IconForms],
                /* Las del asistente también aquí: «24 h antes», no «Hasta 24 h
                   antes». */
                [t('bookings.svc.detail.fact.cancel'), service.booking.cancelWindowHours > 0
                  ? t('svcw.cancel.beforeAmount', { amount: minutesLabel(service.booking.cancelWindowHours * 60) })
                  : t('svcw.cancel.noLimit'), 5, 'cancel', IconCalendarX],
                /* El recordatorio, con las mismas palabras que el asistente. Sin
                   el dato —un worker anterior a la columna— se lee encendido,
                   que es como nace en la base. */
                [t('bookings.svc.detail.fact.reminder'), service.booking.reminderEnabled === false
                  ? t('svcw.set.reminderOff')
                  : t('svcw.cancel.beforeAmount', { amount: minutesLabel((service.booking.reminderHours ?? 24) * 60) }), 1, 'reminder', IconBell],
                [t('bookings.svc.detail.fact.refund'), service.booking.paymentMode === 'onsite'
                  ? t('svcw.cancel.notApplicable')
                  : service.booking.refundPercent === 100
                    ? t('bookings.svc.detail.refundFull')
                    : service.booking.refundPercent === 0
                      ? t('bookings.svc.detail.refundNone')
                      : t('bookings.svc.detail.refundPartial', { percent: service.booking.refundPercent }), 5, 'refund', IconArrowBackUp],
                [t('bookings.svc.detail.policyLate'), service.booking.latePolicy === 'contact'
                  ? t('bookings.svc.detail.policyLateContact')
                  : service.booking.latePolicy === 'move'
                    ? t('bookings.svc.detail.policyLateMove')
                    : t('bookings.svc.detail.policyLateNone'), 5, 'late', IconAlertCircle],
                /* Los tres que vivían aquí como campos sueltos, ahora leídos como
                   los demás: se contestan en el asistente. */
                [t('bookings.svc.detail.email'), service.booking.contactEmail || t('svcw.cancel.contactDefault'), 5, 'email', IconMail],
                [t('bookings.svc.detail.phone'), service.booking.contactPhone || t('common.none'), 5, 'phone', IconPhone],
                [t('bookings.page.theme'), service.bookingPage.theme === 'light'
                  ? t('bookings.page.themeLight')
                  : service.bookingPage.theme === 'dark'
                    ? t('bookings.page.themeDark')
                    : t('bookings.page.themeAuto'), 0, 'theme', IconPalette],
                /* Lo propio de este servicio —«Qué incluye»…—, a su paso del
                   asistente. Ver `OfferCustomFields`. */
                ...(offerFields?.bookings ?? []).map((field) => {
                  const value = service.customValues?.[field.id] ?? ''
                  const shown = !value ? t('offerFields.unset')
                    : field.type === 'image' ? t('offerFields.imageSet')
                      : value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
                  return [field.label, shown, 6, 'custom', IconListDetails] as const
                }),
              ] as const).map(([label, value, step, kind, Icon]) => (
                <div key={label} className={`is-${kind}`}>
                  <span><Icon size={14} aria-hidden="true" />{label}</span>
                  <strong title={value}>{value}</strong>
                  <button
                    type="button"
                    className="dash-secondary-btn dash-summary-action dash-ofd-row-edit"
                    aria-label={t('bookings.svc.detail.editFact', { label: label.toLowerCase() })}
                    onClick={() => openWizard(step)}
                  >
                    <IconPencil size={12} aria-hidden="true" />
                    <span>{t('offers.detail.edit')}</span>
                  </button>
                </div>
              ))}

              {/* Y si se ve o no, como una fila más: el mismo mando que la
                  cabecera de arriba, no un tercer estilo de interruptor. */}
              <div className="is-visibility">
                <span><IconEye size={14} aria-hidden="true" />{t('offers.detail.visibility')}</span>
                <OfferStatusSelect
                  status={service.status}
                  busy={saving === 'status'}
                  blocked={publishBlock}
                  label={service.title}
                  {...(connectingStripe === undefined ? {} : { connecting: connectingStripe })}
                  onSelect={(status) => { void patch({ status }, 'status') }}
                  onConnect={onConnectStripe}
                  onOpenPlans={onOpenPlans}
                />
              </div>
            </div>
          </OfferCard>

          {/* Detalles técnicos, con las mismas filas que la tarjeta de arriba.
              La dirección no está aquí: vive en la pestaña «Página», junto al
              título de SEO y la vista previa al compartir. */}
          <OfferCard className="dash-ofd-meta-card" icon={<IconListDetails size={15} />} title={t('offers.detail.card.meta')}>
            <div className="dash-svc-review">
              <div className="is-id">
                <span><IconFingerprint size={14} aria-hidden="true" />{t('offers.detail.id')}</span>
                <strong><code title={service.id}>{service.id}</code></strong>
                <button
                  type="button"
                  className="dash-secondary-btn dash-summary-action dash-ofd-row-edit"
                  aria-label={t('offers.detail.copyIdAria')}
                  onClick={() => { void navigator.clipboard?.writeText(service.id); showToast(t('offers.detail.idCopied')) }}
                >
                  <IconCopy size={12} aria-hidden="true" />
                  <span>{t('offers.detail.copy')}</span>
                </button>
              </div>
              <div>
                <span><IconUsers size={14} aria-hidden="true" />{t('bookings.svc.detail.fact.clients')}</span>
                <strong>{bookings.length}</strong>
              </div>
              <div>
                <span><IconCalendarPlus size={14} aria-hidden="true" />{t('offers.detail.created')}</span>
                <strong>{longDate(service.createdAt)}</strong>
              </div>
              <div>
                <span><IconHistory size={14} aria-hidden="true" />{t('offers.detail.updated')}</span>
                <strong>{longDate(service.updatedAt)}</strong>
              </div>
            </div>

            {/*
              El historial de la política, plegado y al final.

              Es lo único de esta pantalla que no describe cómo está el servicio
              ahora sino cómo estuvo, y por eso vive con lo técnico y cerrado:
              quien viene a cambiar algo no lo necesita, y quien discute una
              cancelación viene a buscarlo.
            */}
            {history && history.length > 0 ? (
              <details className="dash-ofd-history-details">
                <summary>{t('bookings.svc.detail.history')}</summary>
                <ol className="dash-policy-history">
                  {history.map((row, index) => (
                    <li key={row.id}>
                      <span className="dash-policy-when">
                        <strong>{longDate(row.createdAt)}</strong>
                        <small>{new Date(row.createdAt).toLocaleTimeString(activeLanguage(), { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })}</small>
                        {index === 0 ? <em>{t('bookings.svc.detail.historyCurrent')}</em> : null}
                      </span>
                      <p>{row.policy.text || t('bookings.svc.detail.historyNoText')}</p>
                    </li>
                  ))}
                </ol>
              </details>
            ) : null}
          </OfferCard>

          {/* Zona de peligro */}
          <OfferCard icon={<IconTrash size={15} />} title={t('bookings.svc.detail.delete')} danger>
            <div className="dash-offer-side-state">
              <p>
                {t('bookings.svc.detail.deleteCopy1')} <code>/book/{service.slug}</code>{t('bookings.svc.detail.deleteCopy2')}
                {' '}
                {upcomingCount === 1
                  ? t('bookings.svc.detail.deleteKeepOne')
                  : upcomingCount > 1
                    ? t('bookings.svc.detail.deleteKeepMany', { count: upcomingCount })
                    : t('bookings.svc.detail.deleteKeepNone')}
              </p>
              <button type="button" className="dash-danger-btn" onClick={() => setConfirmDelete(true)}>
                <IconTrash size={14} /> {t('bookings.svc.detail.deleteBtn')}
              </button>
            </div>
          </OfferCard>
        </div>
      )}
      </main>

          {/*
            Quién es este servicio: a la derecha, en vertical y a la vista desde las
            tres pestañas.

            Es la misma tarjeta que la ficha de una oferta y en el mismo sitio.
            Pasó por una columna de siete recuadros con atajo y por una cabecera
            horizontal encima de las pestañas; lo que queda es lo mínimo para saber
            qué se está mirando —portada, nombre, si se ve, descripción y los tres
            datos de pasada— más los mandos de siempre. Los atajos a cada paso del
            asistente están en la pestaña «Ajustes», que es donde una oferta los
            tiene.
          */}
        {/* En «Apariencia» la tarjeta se oculta en pantallas anchas —ahí se mira
            una página entera— pero se conserva en el teléfono, donde va sobre las
            pestañas: si desapareciera, las pestañas subirían de sitio al cambiar
            de una a otra. La oculta el CSS, no el marcado. */}
        <section className={`dash-record-hero is-vertical${tab === 'apariencia' ? ' is-appearance-hidden' : ''}`} aria-label={service.title}>
          <button
            type="button"
            className="dash-record-hero-cover"
            aria-label={t(service.images[0] ? 'bookings.svc.detail.coverChangeAria' : 'bookings.svc.detail.coverPickAria')}
            disabled={saving === 'image'}
            onClick={() => setImagePickerOpen(true)}
          >
            {saving === 'image' ? (
              <IconLoader2 className="dash-spin" size={18} />
            ) : service.images[0] ? (
              <>
                <img src={service.images[0]} alt="" />
                <span className="dash-record-hero-cover-mark" aria-hidden="true">
                  <IconPhotoPlus size={17} />
                  <span>{t('common.changeImage')}</span>
                </span>
              </>
            ) : (
              <IconPhotoPlus size={18} aria-hidden="true" />
            )}
          </button>
          {/* Las demás fotos, debajo de la portada. Ver `RecordPhotoStrip`. */}
          <RecordPhotoStrip
            projectId={projectId}
            token={token}
            images={service.images}
            busy={saving === 'image'}
            onChange={(images) => void patch({ images }, 'image')}
          />

          <div className="dash-record-hero-copy">
            {/* Qué es: en el teléfono la cabecera de la página pierde su título y su
                subtítulo, y esto es lo que queda de ellos. */}
            <span className="dash-record-hero-kind">{t('offers.kind.booking')}</span>
            <div className="dash-record-hero-titleline">
              <h2 title={service.title}>{service.title}</h2>
              <OfferStatusSelect
                status={service.status}
                busy={saving === 'status'}
                blocked={publishBlock}
                label={service.title}
                {...(connectingStripe === undefined ? {} : { connecting: connectingStripe })}
                onSelect={(status) => { void patch({ status }, 'status') }}
                onConnect={onConnectStripe}
                onOpenPlans={onOpenPlans}
              />
            </div>
            {/* Sin descripción no se pinta nada: un «Sin descripción» en gris ocupa
                el sitio del texto que falta para informar de que falta algo que en
                realidad no hace falta. */}
            {offerShortDescription(service) ? (
              <p title={offerShortDescription(service)}>{offerShortDescription(service)}</p>
            ) : null}

            {/* Los tres datos que se consultan de pasada: cuánto dura, para cuánta
                gente y cuánto cuesta. Cada uno se cambia en su paso del asistente,
                al que lleva «Ajustes» aquí al lado. */}
            <ul className="dash-record-hero-meta">
              <li>{service.booking.durationMinutes ? minutesLabel(service.booking.durationMinutes) : t('common.none')}</li>
              <li>
                {service.booking.capacity > 1
                  ? t('svcw.page.spots', { count: service.booking.capacity })
                  : t('svcw.set.single')}
              </li>
              {priceLabel ? <li>{priceLabel}</li> : null}
              {onsiteNote ? <li>{onsiteNote}</li> : null}
            </ul>

            {/* Quién lo da: la cara y el nombre de cada persona del equipo que
                atiende este servicio, la misma regla que la página de reserva
                (`offerResources` en el worker). */}
            <BookingServiceTeam
              projectId={projectId}
              token={token}
              resources={resources}
              members={serviceTeam}
              busy={saving === 'team'}
              onAssign={(ids) => { void patch({ bookingResourceIds: activeTeam.length > 1 ? ids : [], bookingResourceId: ids[0] ?? null }, 'team') }}
              onResourcesChange={onResourcesChange}
            />
          </div>

          <div className="dash-record-hero-actions">
            {publicUrl ? (
              <div className="dash-record-hero-linkgroup">
                <button type="button" className="is-link-action" aria-label={t('offers.detail.link.copy')} onClick={() => { void copyLink() }} title={publicUrl}>
                  <IconCopy size={14} /><span>{t('offers.detail.link.copy')}</span>
                </button>
                {/* Abrir sólo si está publicado: en borrador el enlace contesta
                    «esto no está» y parecería que se rompió. */}
                {live ? (
                  <a href={publicUrl} target="_blank" rel="noreferrer" title={publicUrl} className="is-link-action" aria-label={t('offers.detail.link.open')}>
                    <IconExternalLink size={14} /><span>{t('offers.detail.link.open')}</span>
                  </a>
                ) : null}
              </div>
            ) : onOpenPublish ? (
              <div className="dash-record-hero-linkgroup">
                <button
                  type="button"
                  onClick={() => { if (!blockUnsaved()) onOpenPublish() }}
                  title={t('offers.detail.link.needSite')}
                >
                  <IconWorld size={14} />
                  <span>{t('offers.detail.link.publishToGetLink')}</span>
                </button>
              </div>
            ) : (
              <small className="dash-ofd-need-site">{t('bookings.svc.detail.needSite')}</small>
            )}
            {/* «Ajustes» abre el ASISTENTE, no la pestaña: lo que se quiere al
                pulsarlo es cambiar el servicio, y la pestaña sólo lo enseña. */}
            <button type="button" className="dash-primary-btn" onClick={() => openWizard(0)}>
              <IconSettings size={15} /> {t('offers.detail.tab.settings')}
            </button>
          </div>
        </section>
      </div>

      {editOpen ? (
        <NewBookingServiceDialog
          projectId={projectId}
          token={token}
          siteOrigin={siteOrigin}
          timezone={bookingTimezone}
          resources={resources}
          service={service}
          initialStep={editStep}
          plan={plan}
          publishedOffers={publishedOffers}
          onResourcesChange={onResourcesChange}
          onClose={() => { setEditOpen(false); if (service.setupPending) onBack() }}
          onCreated={(updated) => { setEditOpen(false); onChange(updated) }}
          bookings={bookings}
          canConnectStripe={canConnectStripe ?? (publishBlock?.kind !== 'plan')}
          onOpenPlans={onOpenPlans}
        />
      ) : null}

      {confirmDelete ? (
        <ConfirmDialog
          tone="danger"
          title={t('bookings.svc.detail.deleteAsk')}
          message={(
            /* Partido porque el nombre del servicio va en negrita al principio
               de la frase: una sola clave con marcado dentro no la traduce nadie
               sin escribir HTML. */
            <>
              <strong>{service.title}</strong> {t('bookings.svc.detail.deleteWarn')}
              {upcomingCount === 1 ? (
                <> {t('bookings.svc.detail.deleteWarnOne')}</>
              ) : upcomingCount > 1 ? (
                <> {t('bookings.svc.detail.deleteWarnMany', { count: upcomingCount })}</>
              ) : null}
            </>
          )}
          confirmLabel={t('bookings.svc.detail.deleteConfirm')}
          confirmingLabel={t('bookings.svc.detail.deleting')}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async () => {
            try {
              await deleteOffer(projectId, service.id, token)
              showToast(t('bookings.svc.detail.deletedToast'))
              // Lo quita de la lista y vuelve a ella: la ficha que se está
              // mirando ya no existe, y dejarla ahí enseñaría un fantasma.
              onDeleted(service.id)
            } catch (error) {
              showToast(bookingError(error), 'error')
              setConfirmDelete(false)
            }
          }}
        />
      ) : null}

      {addOpen ? (
        <NewBookingDialog
          projectId={projectId}
          token={token}
          service={service}
          timezone={bookingTimezone}
          resources={resources}
          onClose={() => setAddOpen(false)}
          onCreated={() => { setAddOpen(false); onBookingsChange() }}
        />
      ) : null}

      {imagePickerOpen ? (
        <ImageLibraryPickerDialog
          token={token}
          projectId={projectId}
          onSelect={(url) => void patch(
            { images: [url, ...service.images.filter((image) => image !== url)].slice(0, 8) },
            'image',
          )}
          onClose={() => setImagePickerOpen(false)}
        />
      ) : null}

      {/* El cupo va en la ventana de plan: no se arregla conectando nada. */}
      {explainingPublish?.kind === 'limit' ? (
        <PlanLimitModal
          resource={t('usage.stat.offers').toLowerCase()}
          kind="offers"
          currentPlan={explainingPublish.plan}
          onClose={() => setExplainingPublish(null)}
          onOpenPlans={() => { setExplainingPublish(null); onOpenPlans() }}
        />
      ) : explainingPublish ? (
        <PublishBlockDialog
          kind={explainingPublish.kind}
          itemType="booking"
          connecting={connectingStripe ?? false}
          onConnect={() => {
            setExplainingPublish(null)
            onConnectStripe()
          }}
          onOpenPlans={() => {
            setExplainingPublish(null)
            onOpenPlans()
          }}
          onClose={() => setExplainingPublish(null)}
        />
      ) : null}
    </div>
  )
}
